// DataXing Panel — Routes Configuration
// Roles: Admin (12 modules), PM (9 modules), Sub-admin (9 modules)
// Per DX_Modules.xlsx specification

import UserProfile from "./views/UserProfile/UserProfile";
import AdminSettings from "./views/UserProfile/AdminSettings";
import { PartnersList } from "./Components/Partners/PartnersList";
import { ClientsList } from "Components/Clients/ClientsList";
import { Surveys } from "./Components/Surveys/Surveys";
import { Samples } from "./Components/Samples/Samples";
import { PeopleTwoTone, Dashboard, AccountCircle, ListAlt, Category, Business, Security, Assessment, DeleteForever, Edit, SupervisorAccount, Assignment, Block } from "@material-ui/icons";
import { DashboardSurvey } from "./Components/Surveys/DashboardSurvey";
import AdminDashboard from "./views/Dashboard/AdminDashboard";
import { PMDashboard } from "./Components/Dashboard/PMDashboard";
import {SurveyStatus} from "./Components/SurveyRewardsOverview/SurveyStatus";
import PreScreen from "Components/PreScreen/PreScreen";
import SurveyHold from "./Components/SurveyHold/SurveyHold";
import PartnerSurvey from "./Components/Partners/PartnerSurvey";
import { PartnerClientAnalysis } from "./Components/Partners/PartnerClientAnalysis";
import { AllPanelists } from "./Components/Panelists/AllPanelists";
import ChangeLogPage from "./Components/ChangeLog/ChangeLogPage";
import { RedirectHealth } from "./Components/RedirectHealth/RedirectHealth";

// ========================
// Shared modules (7) — common to Admin, PM, Sub-admin
// ========================
const sharedAdminPaths = [
  {
    // Landing page for every role. Shows KPIs + projects-by-status for the
    // logged-in user's own projects only (filtered by `createdby` on the
    // backend). Redirect `/` -> `/${role}/dashboard` in Layout/Admin.js lands
    // here.
    path: "/dashboard",
    name: "Dashboard",
    hindi: "डैशबोर्ड",
    icon: Dashboard,
    component: PMDashboard,
  },
  {
    // Schedule Survey moved off `/dashboard` so the landing page can host the
    // per-user Dashboard. Sidebar label and component behavior unchanged.
    path: "/schedule-survey",
    name: "Schedule Survey",
    hindi: "सर्वे की योजना",
    icon: Dashboard,
    component: DashboardSurvey,
  },
  {
    path: "/surveys",
    name: "Surveys",
    hindi: "सर्वे",
    icon: ListAlt,
    component: Surveys,
  },
  // {
  //   path: "/samples",
  //   name: "Samples",
  //   hindi: "प्रतिरूप",
  //   icon: Category,
  //   component: Samples,
  // },
  {
    path: "/partners",
    name: "Partners",
    hindi: "साथी",
    icon: Business,
    component: PartnersList,
  },
  {
    path: "/clients",
    name: "Clients",
    hindi: "ग्राहक",
    icon: PeopleTwoTone,
    component: ClientsList,
  },
  {
    path: "/survey-overview",
    name: "Survey Overview",
    hindi: "सर्वे अवलोकन",
    icon: Dashboard,
    component: SurveyStatus,
  },
  {
    path: "/preScreenQuesAns",
    name: "PreScreen Questions",
    hindi: "परिस्कर्ीन सवाल",
    icon: Security,
    component: PreScreen,
  },
  {
    // Universal returns B2: partner entries vs returns, read-only.
    path: "/redirect-health",
    name: "Redirect Health",
    hindi: "रीडायरेक्ट स्थिति",
    icon: Assessment,
    component: RedirectHealth,
  },
  {
    // Audit trail — who changed what, when. Shared across all roles.
    path: "/change-logs",
    name: "Change Log",
    hindi: "परिवर्तन लॉग",
    icon: Assignment,
    component: ChangeLogPage,
  },
];

// Helper: stamp layout + type onto shared paths
const withRole = (routes, layout, type) =>
  routes.map((r) => ({ ...r, layout, type }));

// ========================
// ADMIN — 12 modules (7 shared + 5 admin-only)
// ========================
const adminOnlyRoutes = [
  {
    // #8: Deletion of Projects → SurveyHold component (survey hold/deletion management)
    path: "/deletion-of-projects",
    name: "Deletion of Projects",
    hindi: "परियोजनाओं का विलोपन",
    icon: DeleteForever,
    component: SurveyHold,
    layout: "/admin",
    type: 'admin',
  },
  {
    // #9: Reports Page → AdminDashboard component (admin analytics/reports)
    path: "/reports",
    name: "Reports Page",
    hindi: "रिपोर्ट पृष्ठ",
    icon: Assessment,
    component: AdminDashboard,
    layout: "/admin",
    type: 'admin',
  },
  {
    path: "/partner-client-analysis",
    name: "Partner Client Analysis",
    hindi: "साझेदार ग्राहक विश्लेषण",
    icon: Assessment,
    component: PartnerClientAnalysis,
    layout: "/admin",
    type: 'admin',
  },
  {
    // #11: Prescreen Questions Edit → PreScreen component (admin prescreen question CRUD)
    path: "/preScreenQuesEdit",
    name: "PreScreen Questions Edit",
    hindi: "परिस्कर्ीन सवाल संपादन",
    icon: Edit,
    component: PreScreen,
    layout: "/admin",
    type: 'admin',
  },
  {
    // #12: Access Control for All → AllPanelists component (user listing with roles)
    // TODO: AllPanelists is the closest user-listing component; ideally needs role-management UI
    path: "/access-control",
    name: "Access Control for All",
    hindi: "सभी के लिए एक्सेस नियंत्रण",
    icon: SupervisorAccount,
    component: AllPanelists,
    layout: "/admin",
    type: 'admin',
  },
];

// ========================
// PM — 9 modules (7 shared + 2 PM-only)
// ========================
const pmOnlyRoutes = [
  {
    // #8: Partner PO → PartnerSurvey component (partner purchase order / partner-survey config)
    // TODO: PartnerSurvey manages partner-survey relationships — closest match for Partner PO
    path: "/partner-po",
    name: "Partner PO",
    hindi: "साझेदार पीओ",
    icon: Assignment,
    component: PartnerSurvey,
    layout: "/pm",
    type: 'pm',
  },
  {
    // #9: Survey Exclusion → SurveyHold component (held/excluded surveys management)
    // TODO: SurveyHold manages surveys on hold — closest match for survey exclusion/blacklisting
    path: "/survey-exclusion",
    name: "Survey Exclusion",
    hindi: "सर्वे बहिष्करण",
    icon: Block,
    component: SurveyHold,
    layout: "/pm",
    type: 'pm',
  },
];

// ========================
// SUB-ADMIN — 9 modules (7 shared + 2 sub-admin-only)
// ========================
const subAdminOnlyRoutes = [
  {
    // #8: Deletion of Projects → same as Admin
    path: "/deletion-of-projects",
    name: "Deletion of Projects",
    hindi: "परियोजनाओं का विलोपन",
    icon: DeleteForever,
    component: SurveyHold,
    layout: "/sub-admin",
    type: 'sub-admin',
  },
  {
    // #9: Prescreen Questions Edit → same as Admin
    path: "/preScreenQuesEdit",
    name: "PreScreen Questions Edit",
    hindi: "परिस्कर्ीन सवाल संपादन",
    icon: Edit,
    component: PreScreen,
    layout: "/sub-admin",
    type: 'sub-admin',
  },
];

// ========================
// Utility routes (not in sidebar — isVisible: false)
// ========================
const utilityRoutes = [
  {
    path: "/profile",
    name: "User Profile",
    hindi: "उपयोगकर्ता प्रोफ़ाइल",
    icon: AccountCircle,
    component: UserProfile,
    layout: "/admin",
    type: 'admin',
    isVisible: false,
  },
  {
    path: "/settings",
    name: "Settings",
    hindi: "सेटिंग्स",
    icon: AccountCircle,
    component: AdminSettings,
    layout: "/admin",
    type: 'admin',
    isVisible: false,
  },
  {
    path: "/profile",
    name: "User Profile",
    hindi: "उपयोगकर्ता प्रोफ़ाइल",
    icon: AccountCircle,
    component: UserProfile,
    layout: "/pm",
    type: 'pm',
    isVisible: false,
  },
  {
    path: "/settings",
    name: "Settings",
    hindi: "सेटिंग्स",
    icon: AccountCircle,
    component: AdminSettings,
    layout: "/pm",
    type: 'pm',
    isVisible: false,
  },
  {
    path: "/profile",
    name: "User Profile",
    hindi: "उपयोगकर्ता प्रोफ़ाइल",
    icon: AccountCircle,
    component: UserProfile,
    layout: "/sub-admin",
    type: 'sub-admin',
    isVisible: false,
  },
  {
    path: "/settings",
    name: "Settings",
    hindi: "सेटिंग्स",
    icon: AccountCircle,
    component: AdminSettings,
    layout: "/sub-admin",
    type: 'sub-admin',
    isVisible: false,
  },
];

const dashboardRoutes = [
  // Admin: 7 shared + 5 admin-only = 12 sidebar items
  ...withRole(sharedAdminPaths, "/admin", "admin"),
  ...adminOnlyRoutes,

  // PM: 7 shared + 2 PM-only = 9 sidebar items
  ...withRole(sharedAdminPaths, "/pm", "pm"),
  ...pmOnlyRoutes,

  // Sub-admin: 7 shared + 2 sub-admin-only = 9 sidebar items
  ...withRole(sharedAdminPaths, "/sub-admin", "sub-admin"),
  ...subAdminOnlyRoutes,

  // Utility routes (hidden from sidebar)
  ...utilityRoutes,
];

export default dashboardRoutes;
